import { inject, Injectable } from "@angular/core";
import { PrintingApi } from "../Api/PrintingApi";
import { PrintingState } from "./PrintingState";
import { PrintingSearchRequest } from "../Dto/PrintingSearchRequest";
import { CreatePrintingRequest, UpdatePrintingRequest, InspectPrintingRequest } from "../Dto/CreatePrintingRequest";
import { PrintingEntity } from "../Dto/PrintingModel";
import { createBackendError, getBackendErrorMessage } from "../../../Common/Utility/UtilityError";

@Injectable({ providedIn: "root" })
export class PrintingEffects {
  private readonly printingApi = inject(PrintingApi);
  private readonly printingState = inject(PrintingState);

  async searchPrintings(params: PrintingSearchRequest = {}): Promise<void> {
    const merged = { page: 1, limit: 10, ...params };
    this.printingState.setLoading(true);
    this.printingState.setError(null);

    try {
      const response = await this.printingApi.searchPrintings(merged);
      this.printingState.setPrintings(response.data, response.pagination);
      this.printingState.setSearchParams(merged);
    } catch (exception: unknown) {
      this.printingState.setError(getBackendErrorMessage(exception));
    } finally {
      this.printingState.setLoading(false);
    }
  }

  async createPrinting(payload: CreatePrintingRequest): Promise<PrintingEntity> {
    try {
      const created = await this.printingApi.createPrinting(payload);
      this.printingState.addPrinting(created);
      return created;
    } catch (exception: unknown) {
      this.printingState.setError(getBackendErrorMessage(exception));
      throw createBackendError(exception);
    }
  }

  async updatePrinting(id: string, payload: UpdatePrintingRequest): Promise<PrintingEntity> {
    try {
      const updated = await this.printingApi.updatePrinting(id, payload);
      this.printingState.updatePrinting(updated);
      return updated;
    } catch (exception: unknown) {
      this.printingState.setError(getBackendErrorMessage(exception));
      throw createBackendError(exception);
    }
  }

  async getPrintingById(id: string): Promise<PrintingEntity | null> {
    try {
      return await this.printingApi.getPrintingById(id);
    } catch (exception: unknown) {
      this.printingState.setError(getBackendErrorMessage(exception));
      throw createBackendError(exception);
    }
  }

  async confirmPrinting(id: string): Promise<PrintingEntity> {
    try {
      const updated = await this.printingApi.confirmPrinting(id);
      this.printingState.updatePrinting(updated);
      return updated;
    } catch (exception: unknown) {
      this.printingState.setError(getBackendErrorMessage(exception));
      throw createBackendError(exception);
    }
  }

  async shipPrinting(id: string): Promise<PrintingEntity> {
    try {
      const updated = await this.printingApi.shipPrinting(id);
      this.printingState.updatePrinting(updated);
      return updated;
    } catch (exception: unknown) {
      this.printingState.setError(getBackendErrorMessage(exception));
      throw createBackendError(exception);
    }
  }

  async inspectPrinting(id: string, payload: InspectPrintingRequest): Promise<PrintingEntity> {
    try {
      const updated = await this.printingApi.inspectPrinting(id, payload);
      this.printingState.updatePrinting(updated);
      return updated;
    } catch (exception: unknown) {
      this.printingState.setError(getBackendErrorMessage(exception));
      throw createBackendError(exception);
    }
  }

  async deletePrinting(id: string): Promise<boolean> {
    try {
      await this.printingApi.deletePrinting(id);
      this.printingState.removePrinting(id);
      return true;
    } catch (exception: unknown) {
      this.printingState.setError(getBackendErrorMessage(exception));
      throw createBackendError(exception);
    }
  }
}
